import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, ExternalLink, AlertTriangle, Pencil, Send, Copy, Eye, Clock, CheckCircle2, Printer, FileText } from "lucide-react";
import { Estimate, isNewFormat, SubLine, LineItem, useUpdateEstimate, EstimateGroup, EstimatePrintSettings, DEFAULT_PRINT_SETTINGS } from "@/hooks/useEstimates";
import { QBEstimate } from "@/hooks/useQBEstimates";
import { SendEstimateDialog } from "./SendEstimateDialog";
import { EstimatePrintTemplate, EstimatePrintData, EstimatePrintGroup, EstimatePrintLineItem } from "@/components/shared/print-templates/EstimatePrintTemplate";
import { exportPdf } from "@/lib/export";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import {
  DEFAULT_COMMERCIAL_EXCLUSIONS,
  DEFAULT_CLARIFICATIONS,
  parseProposalNotes,
  isAlternateGroup,
  parseLocation,
  fmtMoney,
} from "@/lib/estimateProposal";

const fmt = (n: number | null | undefined) =>
  n != null ? `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—";

const safeDate = (d: string | null) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return "—"; }
};

const safeDateFull = (d: string | null) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return "—"; }
};

const statusClass: Record<string, string> = {
  Draft: "bg-muted text-muted-foreground",
  Sent: "bg-blue-100 text-blue-800",
  Accepted: "bg-emerald-100 text-emerald-800",
  Rejected: "bg-destructive/15 text-destructive",
  Closed: "bg-muted text-muted-foreground",
  Converted: "bg-violet-100 text-violet-800",
  Pending: "bg-yellow-100 text-yellow-800",
};

const syncDotClass: Record<string, string> = {
  synced: "bg-emerald-500", pending: "bg-yellow-500", error: "bg-destructive", not_synced: "bg-muted-foreground",
};

const STATUSES = ["Draft", "Sent", "Accepted", "Rejected", "Closed", "Converted"];

export type UnifiedEstimate = {
  source: "cmdx" | "qb";
  id: string;
  number: string | null;
  customerName: string | null;
  date: string | null;
  expiryDate?: string | null;
  status: string;
  syncStatus?: string | null;
  totalAmount: number | null;
  subtotal?: number | null;
  taxRate?: number | null;
  taxAmount?: number | null;
  notes?: string | null;
  lineItems: any[];
  estimateGroups?: EstimateGroup[];
  printSettings?: EstimatePrintSettings | null;
  projectName?: string | null;
  projectAddress?: string | null;
  qbId?: string | null;
  // Tracking fields
  sentAt?: string | null;
  sentToName?: string | null;
  sentToEmail?: string | null;
  viewedAt?: string | null;
  viewCount?: number;
  approvedAt?: string | null;
  approvedByName?: string | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  customerNotes?: string | null;
  shareToken?: string | null;
};

export function unifyEstimate(e: Estimate): UnifiedEstimate {
  const raw = e as any;
  return {
    source: "cmdx", id: e.id, number: e.estimate_number, customerName: e.customers?.name ?? null,
    date: e.txn_date, expiryDate: e.expiry_date, status: e.status, syncStatus: e.qb_sync_status,
    totalAmount: e.total_amount, subtotal: e.subtotal, taxRate: raw.tax_rate, taxAmount: raw.tax_amount,
    notes: e.notes, lineItems: e.line_items ?? [], qbId: e.qb_estimate_id,
    estimateGroups: (e as any).estimate_groups ?? [],
    printSettings: (e as any).print_settings ?? null,
    projectName: raw.projects?.name ?? raw.project_name ?? null,
    projectAddress: raw.projects?.address ?? raw.project_address ?? null,
    sentAt: raw.sent_at, sentToName: raw.sent_to_name, sentToEmail: raw.sent_to_email,
    viewedAt: raw.viewed_at, viewCount: raw.view_count ?? 0,
    approvedAt: raw.approved_at, approvedByName: raw.approved_by_name,
    rejectedAt: raw.rejected_at, rejectionReason: raw.rejection_reason,
    customerNotes: raw.customer_notes, shareToken: raw.share_token,
  };
}

export function unifyQBEstimate(e: QBEstimate): UnifiedEstimate {
  const st = e.txn_status || e.status || "Pending";
  return {
    source: "qb", id: e.id, number: e.doc_number, customerName: e.customer_ref_name || e.customer_qb_id,
    date: e.txn_date, status: st, totalAmount: e.total_amount, subtotal: e.total_amount, taxRate: 0, taxAmount: 0,
    lineItems: (e.line_items as any[]) ?? [], qbId: e.quickbooks_id,
    estimateGroups: [],
    printSettings: null,
  };
}

interface Props {
  estimate: UnifiedEstimate | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onConvert?: () => void;
  onEdit?: () => void;
  onDuplicate?: () => void;
  rawEstimate?: Estimate | null;
}

export const EstimateDetailDrawer = ({ estimate, open, onOpenChange, onConvert, onEdit, onDuplicate, rawEstimate }: Props) => {
  const [viewMode, setViewMode] = useState<"internal" | "customer">("internal");
  const [sendOpen, setSendOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<{ name?: string; address?: string; city?: string; state?: string; zip?: string; phone?: string; logo_url?: string; website?: string } | null>(null);
  const updateEstimate = useUpdateEstimate();

  // Fetch company branding once when the drawer opens
  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data: cid } = await supabase.rpc("my_company_id" as any);
      if (!cid) return;
      const { data: co } = await supabase
        .from("companies")
        .select("name, address, city, state, zip, phone, logo_url, website")
        .eq("id", cid)
        .maybeSingle();
      if (co) setCompanyInfo(co as any);
    })();
  }, [open]);

  if (!estimate) return null;

  const canConvert = (estimate.source === "cmdx" && estimate.status === "Accepted") || (estimate.source === "qb" && ["Accepted", "Closed"].includes(estimate.status));
  const canSend = estimate.source === "cmdx" && ["Draft", "Sent"].includes(estimate.status);

  // Calculate totals
  let laborCost = 0, laborBilled = 0, materialCost = 0, materialBilled = 0;
  estimate.lineItems.forEach((item: any) => {
    if (isNewFormat(item)) {
      laborBilled += item.labor_total_billed ?? item.labor_total ?? 0;
      materialBilled += item.material_total_billed ?? item.material_total ?? 0;
      laborCost += item.labor_total_cost ?? 0;
      materialCost += item.material_total_cost ?? 0;
    } else if (Array.isArray(item.sub_lines)) {
      (item.sub_lines as SubLine[]).forEach((sl) => {
        const billed = sl.total_billed ?? sl.total ?? 0;
        const cost = sl.total_cost ?? 0;
        if (sl.type === "labor") { laborBilled += billed; laborCost += cost; }
        else { materialBilled += billed; materialCost += cost; }
      });
    } else {
      const qty = item.qty ?? item.Qty ?? 0;
      const lp = item.labor_price ?? 0;
      const mp = item.material_price ?? 0;
      const up = item.UnitPrice ?? item.unit_price ?? 0;
      if (item.line_type === "labor") laborBilled += qty * (lp || up);
      else if (item.line_type === "material") materialBilled += qty * (mp || up);
      else { laborBilled += qty * lp; materialBilled += qty * mp; if (!lp && !mp) laborBilled += qty * up; }
    }
  });

  const totalCost = laborCost + materialCost;
  const totalBilled = laborBilled + materialBilled;
  const profit = totalBilled - totalCost;
  const margin = totalBilled > 0 ? (profit / totalBilled) * 100 : 0;
  const marginColor = margin >= 30 ? "text-emerald-600" : margin >= 20 ? "text-yellow-600" : "text-destructive";

  const handleStatusChange = async (newStatus: string) => {
    if (estimate.source !== "cmdx") return;
    try {
      await updateEstimate.mutateAsync({ id: estimate.id, status: newStatus } as any);
      toast.success(`Status updated to ${newStatus}`);
    } catch (err: any) {
      toast.error(err.message || "Failed to update status");
    }
  };

  const copyShareLink = () => {
    if (estimate.shareToken) {
      navigator.clipboard.writeText(`${window.location.origin}/estimate/${estimate.shareToken}`);
      toast.success("Link copied!");
    }
  };

  /** Build EstimatePrintData from the UnifiedEstimate + company info */
  const buildPrintData = (): EstimatePrintData => {
    const storedTaxAmount = Number(estimate.taxAmount ?? (estimate as any).tax_amount ?? 0);
    const storedTaxRate = Number(estimate.taxRate ?? (estimate as any).tax_rate ?? 0);
    let laborSubtotal = 0;
    let materialSubtotal = 0;
    let embeddedSalesTax = 0;
    let hasVendorTaxLines = false;

    (estimate.lineItems ?? []).forEach((item: any) => {
      if (Array.isArray(item.sub_lines) && item.sub_lines.length > 0) {
        item.sub_lines.forEach((sl: any) => {
          const qty = Number(sl.qty ?? 0);
          const billed = Number(sl.total_billed ?? sl.total ?? qty * Number(sl.unit_price ?? 0));
          if (sl.type === "labor") {
            laborSubtotal += billed;
            return;
          }

          materialSubtotal += billed;
          if (sl.apply_vendor_tax === true) hasVendorTaxLines = true;
          if (sl.taxable && sl.apply_vendor_tax && Number(sl.vendor_tax_rate ?? 0) > 0) {
            const taxMultiplier = Number(sl.vendor_tax_rate ?? 0) / 100;
            const effectiveCost = Number(sl.cost_price ?? 0) * (1 + taxMultiplier);
            const preTaxPrice = effectiveCost * (1 + Number(sl.markup_pct ?? 0) / 100);
            embeddedSalesTax += (preTaxPrice * taxMultiplier) * qty;
          }
        });
        return;
      }

      const qty = Number(item.qty ?? item.Qty ?? 0);
      const laborBilled = Number(item.labor_total_billed ?? item.labor_total ?? 0);
      const materialBilled = Number(item.material_total_billed ?? item.material_total ?? 0);

      if (laborBilled || materialBilled) {
        laborSubtotal += laborBilled;
        materialSubtotal += materialBilled;
        return;
      }

      const billed = Number(item.total_billed ?? item.total ?? item.Amount ?? qty * Number(item.UnitPrice ?? item.unit_price ?? 0));
      if (item.line_type === "material") materialSubtotal += billed;
      else laborSubtotal += billed;
    });

    const hasEmbeddedTax = storedTaxAmount === 0 && hasVendorTaxLines;
    const materialSubtotalPreTax = hasEmbeddedTax ? Math.max(materialSubtotal - embeddedSalesTax, 0) : materialSubtotal;
    const salesTaxTotal = hasEmbeddedTax ? embeddedSalesTax : storedTaxAmount;

    const toPrintLine = (item: any, idx: number): EstimatePrintLineItem => {
      const hasSubLines = Array.isArray(item.sub_lines) && item.sub_lines.length > 0;
      const qty = Number(item.qty ?? item.Qty ?? 0);
      let totalBilled = item.total_billed ?? item.total ?? item.Amount ?? 0;
      let laborBilled = 0;
      let materialBilled = 0;
      if (hasSubLines) {
        (item.sub_lines as any[]).forEach((sl) => {
          const b = sl.total_billed ?? sl.total ?? (sl.qty ?? 0) * (sl.unit_price ?? 0);
          if (sl.type === "labor") laborBilled += b;
          else materialBilled += b;
        });
        if (!totalBilled) totalBilled = laborBilled + materialBilled;
      } else {
        laborBilled = item.labor_total_billed ?? item.labor_total ?? 0;
        materialBilled = item.material_total_billed ?? item.material_total ?? 0;
        if (!totalBilled) totalBilled = qty * (item.UnitPrice ?? item.unit_price ?? 0);
      }
      const unit = item.unit || item.UnitOfMeasure || "LS";
      const customerDesc = item.customer_description || item.description || item.Description || `Item ${idx + 1}`;
      const safeQty = qty || 1;
      return {
        line_number: Number(item.line_num ?? idx + 1),
        label: item.label || undefined,
        description: item.description || item.Description || customerDesc,
        customer_description: customerDesc,
        quantity: qty,
        unit,
        labor_unit_price: laborBilled / safeQty,
        material_unit_price: materialBilled / safeQty,
        unit_price: Number(totalBilled) / safeQty,
        amount: Number(totalBilled) || 0,
        location: parseLocation(customerDesc),
        sub_lines: Array.isArray(item.sub_lines)
          ? item.sub_lines.map((sl: any) => ({ type: sl.type, description: sl.description }))
          : undefined,
      };
    };

    const groups = estimate.estimateGroups ?? [];
    const items = estimate.lineItems ?? [];
    let printGroups: EstimatePrintGroup[] | undefined;
    let flatLines: EstimatePrintLineItem[] | undefined;

    if (groups.length > 0) {
      const sorted = [...groups].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      printGroups = sorted
        .map((g) => {
          const groupItems = items.filter((it: any) => it.group_id === g.id);
          if (groupItems.length === 0) return null;
          const lines = groupItems.map((it: any, i: number) => toPrintLine(it, i));
          const subtotal = lines.reduce((s, l) => s + l.amount, 0);
          return { name: g.name || "Untitled Section", color: g.color, lines, subtotal } as EstimatePrintGroup;
        })
        .filter((x): x is EstimatePrintGroup => x !== null);

      const ungrouped = items.filter((it: any) => !it.group_id || !groups.some((g) => g.id === it.group_id));
      if (ungrouped.length > 0) {
        const lines = ungrouped.map((it: any, i: number) => toPrintLine(it, i));
        printGroups.push({
          name: "General Scope",
          lines,
          subtotal: lines.reduce((s, l) => s + l.amount, 0),
        });
      }
    } else {
      flatLines = items.map((it: any, i: number) => toPrintLine(it, i));
    }

    const subtotal = Number(estimate.subtotal ?? estimate.totalAmount ?? 0);
    const settings: EstimatePrintSettings = estimate.printSettings ?? DEFAULT_PRINT_SETTINGS;

    return {
      estimate_number: estimate.number ?? "—",
      estimate_date: estimate.date ?? new Date().toISOString(),
      expiry_date: estimate.expiryDate ?? null,
      status: estimate.status,
      customer_name: estimate.customerName ?? undefined,
      project_name: estimate.projectName ?? undefined,
      project_address: estimate.projectAddress ?? undefined,
      company_name: companyInfo?.name,
      company_logo_url: companyInfo?.logo_url ?? null,
      company_address: companyInfo?.address,
      company_city_state_zip: [companyInfo?.city, companyInfo?.state, companyInfo?.zip].filter(Boolean).join(", ") || undefined,
      company_phone: companyInfo?.phone,
      company_website: companyInfo?.website,
      estimator_name: (estimate as any).estimatorName ?? (estimate as any).estimator_name ?? undefined,
      groups: printGroups,
      flat_lines: flatLines,
      subtotal,
      tax_amount: hasEmbeddedTax ? 0 : storedTaxAmount,
      tax_rate: storedTaxRate,
      total: Number(estimate.totalAmount ?? (subtotal + (hasEmbeddedTax ? 0 : storedTaxAmount))),
      labor_subtotal: laborSubtotal,
      material_subtotal_pre_tax: materialSubtotalPreTax,
      sales_tax_total: salesTaxTotal,
      notes: estimate.notes ?? undefined,
      settings,
    };
  };

  const printProposal = () => {
    const data = buildPrintData();
    exportPdf(<EstimatePrintTemplate data={data} />, `Proposal ${data.estimate_number}`);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-2xl w-full overflow-y-auto">
          <SheetHeader className="pb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <SheetTitle className="flex items-center gap-2">
                {estimate.source === "qb" ? (
                  <Badge variant="outline" className="bg-muted text-muted-foreground text-xs font-mono">QB {estimate.number}</Badge>
                ) : (
                  <Badge className="bg-primary text-primary-foreground text-xs font-mono">{estimate.number}</Badge>
                )}
              </SheetTitle>
              <Badge variant="outline" className={statusClass[estimate.status] ?? "bg-muted text-muted-foreground"}>{estimate.status}</Badge>
              {estimate.syncStatus && (
                <div className="flex items-center gap-1.5">
                  <span className={`h-2 w-2 rounded-full ${syncDotClass[estimate.syncStatus] ?? syncDotClass.not_synced}`} />
                  <span className="text-xs text-muted-foreground capitalize">{estimate.syncStatus}</span>
                </div>
              )}
            </div>
            <div className="flex justify-end mt-2">
              <div className="inline-flex rounded-md border border-border overflow-hidden text-sm">
                <button className={cn("px-3 py-1", viewMode === "internal" ? "bg-primary text-primary-foreground" : "bg-background text-foreground hover:bg-muted")} onClick={() => setViewMode("internal")}>Internal</button>
                <button className={cn("px-3 py-1 border-l border-border", viewMode === "customer" ? "bg-primary text-primary-foreground" : "bg-background text-foreground hover:bg-muted")} onClick={() => setViewMode("customer")}>Customer</button>
              </div>
            </div>
          </SheetHeader>

          <div className="space-y-6">
            {/* Approved Banner */}
            {estimate.status === "Accepted" && estimate.approvedByName && (
              <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <span className="font-semibold text-emerald-800 dark:text-emerald-400">Approved by {estimate.approvedByName}</span>
                </div>
                <p className="text-sm text-muted-foreground">{safeDateFull(estimate.approvedAt ?? null)}</p>
                {estimate.customerNotes && <p className="text-sm italic">"{estimate.customerNotes}"</p>}
                {canConvert && onConvert && (
                  <Button onClick={onConvert} className="mt-2">
                    🚀 Convert to Project <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                )}
              </div>
            )}

            {/* Rejected Banner */}
            {estimate.status === "Rejected" && (
              <div className="p-4 rounded-lg border border-destructive/30 bg-destructive/5 space-y-1">
                <p className="font-semibold text-destructive">❌ Rejected by {estimate.approvedByName}</p>
                <p className="text-sm text-muted-foreground">{safeDateFull(estimate.rejectedAt ?? null)}</p>
                {estimate.rejectionReason && <p className="text-sm italic">"{estimate.rejectionReason}"</p>}
              </div>
            )}

            {/* Tracking Panel */}
            {estimate.sentAt && (
              <div className="p-3 rounded-lg border bg-muted/30 space-y-2 text-sm">
                <p className="font-semibold flex items-center gap-1"><Send className="h-3 w-3" /> Sent to Customer</p>
                <p className="text-muted-foreground">{estimate.sentToName} — {estimate.sentToEmail}</p>
                <p className="text-muted-foreground">Sent: {safeDateFull(estimate.sentAt)}</p>
                {estimate.viewedAt && (
                  <p className="flex items-center gap-1 text-muted-foreground">
                    <Eye className="h-3 w-3" /> Viewed: {safeDateFull(estimate.viewedAt)}
                    {(estimate.viewCount ?? 0) > 1 && <span>(viewed {estimate.viewCount} times)</span>}
                  </p>
                )}
                {estimate.status === "Sent" && (
                  <p className="flex items-center gap-1 text-muted-foreground">
                    <Clock className="h-3 w-3" /> Awaiting response
                  </p>
                )}
                <div className="flex gap-2 mt-1">
                  {canSend && <Button variant="outline" size="sm" onClick={() => setSendOpen(true)}>Resend</Button>}
                  {estimate.shareToken && <Button variant="outline" size="sm" onClick={copyShareLink}><Copy className="h-3 w-3 mr-1" /> Copy Link</Button>}
                </div>
              </div>
            )}

            {viewMode === "customer" ? (
              <CustomerProposalView estimate={estimate} />
            ) : (
              <>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div><span className="text-muted-foreground">Customer</span><p className="font-medium">{estimate.customerName ?? "—"}</p></div>
                  <div><span className="text-muted-foreground">Date</span><p className="font-medium">{safeDate(estimate.date)}</p></div>
                  <div><span className="text-muted-foreground">Expiry</span><p className="font-medium">{safeDate(estimate.expiryDate ?? null)}</p></div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Line Items</h3>
                  {estimate.lineItems.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No line items</p>
                  ) : (
                    estimate.lineItems.map((item: any, idx: number) => {
                      const hasSubLines = Array.isArray(item.sub_lines) && item.sub_lines.length > 0;
                      const desc = item.description || item.Description || `Line ${idx + 1}`;
                      const qty = item.qty ?? item.Qty ?? 0;
                      const unit = item.unit || item.UnitOfMeasure || "";
                      const lineBilled = item.total_billed ?? item.total ?? item.Amount ?? (qty * (item.UnitPrice ?? item.unit_price ?? 0));

                      return (
                        <div key={idx} className="border rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground font-mono w-6">#{item.line_num ?? idx + 1}</span>
                              <span className="font-medium text-sm">{desc}</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm">
                              <span className="text-muted-foreground">{qty} {unit}</span>
                              <span className="font-semibold">{fmt(lineBilled)}</span>
                            </div>
                          </div>
                          {hasSubLines && (item.sub_lines as any[]).map((sl: any, si: number) => {
                            const slCost = sl.cost_price ?? 0;
                            const slBilled = sl.total_billed ?? sl.total ?? 0;
                            const slMarkup = sl.markup_pct ?? 0;
                            return (
                              <div key={sl.id ?? si} className="space-y-0.5">
                                <div className="flex items-center gap-2 pl-8 text-sm">
                                  <Badge variant="outline" className={`text-xs ${sl.type === "labor" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                                    {sl.type === "labor" ? "Labor" : "Matl"}
                                  </Badge>
                                  <span className="flex-1 truncate">{sl.description}</span>
                                  <span className="text-muted-foreground">{sl.qty} {sl.unit}</span>
                                  {slCost > 0 && <span className="text-muted-foreground text-xs">Cost: {fmt(slCost)}</span>}
                                  {slMarkup > 0 && <span className="text-muted-foreground text-xs">{slMarkup}%</span>}
                                  <span className="text-muted-foreground">@ {fmt(sl.unit_price)}</span>
                                  <span className="font-medium">{fmt(slBilled)}</span>
                                </div>
                                {slCost > 0 && (
                                  <p className="text-[11px] text-muted-foreground pl-8 ml-[52px]">
                                    Cost: {fmt(slCost)} → Billed: {fmt(sl.unit_price)} ({slMarkup}% markup)
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })
                  )}
                </div>

                {totalCost > 0 && (
                  <>
                    <Separator />
                    <div className="border rounded-lg p-3 space-y-1 bg-yellow-50/50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800">
                      <div className="flex items-center gap-1 text-xs font-semibold text-yellow-700 dark:text-yellow-400 mb-1">
                        <AlertTriangle className="h-3 w-3" /> INTERNAL — Not shown to customer
                      </div>
                      <div className="flex justify-between text-sm"><span className="text-muted-foreground">Total Cost</span><span>{fmt(totalCost)}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-muted-foreground">Total Revenue</span><span>{fmt(totalBilled)}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-muted-foreground">Gross Profit</span><span className={marginColor}>{fmt(profit)}</span></div>
                      <div className="flex justify-between text-sm font-semibold"><span className="text-muted-foreground">Margin</span><span className={marginColor}>{margin.toFixed(1)}%</span></div>
                    </div>
                  </>
                )}

                {estimate.notes && (
                  <><Separator /><div><h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1">Customer Notes</h3><p className="text-sm whitespace-pre-wrap">{estimate.notes}</p></div></>
                )}

                <Separator />

                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Labor (billed)</span><span>{fmt(laborBilled)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Material (billed)</span><span>{fmt(materialBilled)}</span></div>
                  <div className="flex justify-between font-semibold text-base border-t pt-2 mt-2">
                    <span>Grand Total</span><span>{fmt(estimate.totalAmount)}</span>
                  </div>
                </div>
              </>
            )}

            <Separator />

            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" onClick={() => setPreviewOpen(true)}>
                <FileText className="mr-1 h-4 w-4" /> Preview
              </Button>
              <Button variant="outline" onClick={printProposal}>
                <Printer className="mr-1 h-4 w-4" /> Print Proposal
              </Button>
              {estimate.source === "cmdx" && estimate.status === "Draft" && onEdit && (
                <Button variant="outline" onClick={onEdit}>
                  <Pencil className="mr-1 h-4 w-4" /> Edit Estimate
                </Button>
              )}
              {estimate.source === "cmdx" && onDuplicate && (
                <Button variant="outline" onClick={onDuplicate}>
                  <Copy className="mr-1 h-4 w-4" /> Duplicate
                </Button>
              )}
              {canSend && (
                <Button variant="outline" onClick={() => setSendOpen(true)}>
                  <Send className="mr-1 h-4 w-4" /> Send to Customer
                </Button>
              )}
              {canConvert && !estimate.approvedByName && (
                <Button onClick={onConvert}>Convert to Project <ArrowRight className="ml-1 h-4 w-4" /></Button>
              )}
              {estimate.qbId && (
                <Button variant="outline" asChild>
                  <a href={`https://app.qbo.intuit.com/app/estimate?txnId=${estimate.qbId}`} target="_blank" rel="noopener noreferrer">
                    View in QB <ExternalLink className="ml-1 h-4 w-4" />
                  </a>
                </Button>
              )}
              {estimate.source === "cmdx" && (
                <Select value={estimate.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <SendEstimateDialog
        open={sendOpen}
        onOpenChange={setSendOpen}
        estimate={rawEstimate ?? null}
        onSent={() => {
          setSendOpen(false);
          // Refresh will happen via query invalidation
        }}
      />

      {/* Preview Dialog — renders the full EstimatePrintTemplate before export */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-[9in] max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="px-6 pt-4 pb-2 border-b flex-row items-center justify-between space-y-0">
            <DialogTitle className="text-sm font-semibold">Proposal Preview</DialogTitle>
            <Button size="sm" onClick={printProposal}>
              <Printer className="mr-1 h-4 w-4" /> Print / Download PDF
            </Button>
          </DialogHeader>
          <div className="bg-white text-black">
            <EstimatePrintTemplate data={buildPrintData()} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Professional commercial subcontractor proposal view
// ────────────────────────────────────────────────────────────────────────

interface ProposalLineRow {
  itemCode: string;
  description: string;
  location: string;
  qty: number;
  unit: string;
  totalBilled: number;
}

interface ProposalGroupSection {
  id: string;
  name: string;
  color?: string;
  isAlternate: boolean;
  lines: ProposalLineRow[];
  subtotal: number;
}

function buildProposalSections(estimate: UnifiedEstimate): {
  base: ProposalGroupSection[];
  alternates: ProposalGroupSection[];
  baseTotal: number;
  alternatesTotal: number;
} {
  const groups = estimate.estimateGroups ?? [];
  const items = estimate.lineItems ?? [];

  const toRow = (item: any, idx: number): ProposalLineRow => {
    const hasSubLines = Array.isArray(item.sub_lines) && item.sub_lines.length > 0;
    let totalBilled = item.total_billed ?? item.total ?? item.Amount ?? 0;
    if (!totalBilled && hasSubLines) {
      totalBilled = (item.sub_lines as any[]).reduce(
        (s, sl) => s + (sl.total_billed ?? sl.total ?? (sl.qty ?? 0) * (sl.unit_price ?? 0)),
        0,
      );
    }
    if (!totalBilled) {
      const qty = item.qty ?? item.Qty ?? 0;
      totalBilled = qty * (item.UnitPrice ?? item.unit_price ?? 0);
    }
    const desc = item.customer_description || item.description || item.Description || `Item ${idx + 1}`;
    return {
      itemCode: String(item.line_num ?? idx + 1).padStart(2, "0"),
      description: desc,
      location: parseLocation(desc),
      qty: Number(item.qty ?? item.Qty ?? 0),
      unit: item.unit || item.UnitOfMeasure || "LS",
      totalBilled: Number(totalBilled) || 0,
    };
  };

  const makeSection = (id: string, name: string, color: string | undefined, lines: ProposalLineRow[]): ProposalGroupSection => ({
    id,
    name,
    color,
    isAlternate: isAlternateGroup(name),
    lines,
    subtotal: lines.reduce((s, l) => s + l.totalBilled, 0),
  });

  const sections: ProposalGroupSection[] = [];

  if (groups.length > 0) {
    const sorted = [...groups].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    for (const g of sorted) {
      const groupItems = items.filter((it: any) => it.group_id === g.id);
      if (groupItems.length === 0) continue;
      sections.push(makeSection(g.id, g.name || "Untitled Section", g.color, groupItems.map(toRow)));
    }
    const ungrouped = items.filter((it: any) => !it.group_id || !groups.some((g) => g.id === it.group_id));
    if (ungrouped.length > 0) {
      sections.push(makeSection("__ungrouped", "General Scope", undefined, ungrouped.map(toRow)));
    }
  } else {
    sections.push(makeSection("__all", "Scope of Work", undefined, items.map(toRow)));
  }

  const base = sections.filter((s) => !s.isAlternate);
  const alternates = sections.filter((s) => s.isAlternate);
  const baseTotal = base.reduce((s, sec) => s + sec.subtotal, 0);
  const alternatesTotal = alternates.reduce((s, sec) => s + sec.subtotal, 0);
  return { base, alternates, baseTotal, alternatesTotal };
}

function CustomerProposalView({ estimate }: { estimate: UnifiedEstimate }) {
  const { base, alternates, baseTotal } = buildProposalSections(estimate);
  const parsed = parseProposalNotes(estimate.notes);
  const clarifications = parsed.clarifications.length > 0 ? parsed.clarifications : DEFAULT_CLARIFICATIONS;
  const exclusions = parsed.exclusions.length > 0 ? parsed.exclusions : DEFAULT_COMMERCIAL_EXCLUSIONS;
  const scope = parsed.scope || parsed.remainder || "";

  return (
    <div className="space-y-6 bg-white dark:bg-background text-foreground p-1">
      {/* Header */}
      <div className="border-b-2 border-foreground pb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Commercial Subcontractor Proposal</p>
            <h2 className="text-2xl font-bold tracking-tight mt-1">PROPOSAL</h2>
            <p className="font-mono text-sm font-semibold mt-0.5">{estimate.number ?? "—"}</p>
          </div>
          <div className="text-right text-xs space-y-0.5">
            <p><span className="text-muted-foreground">Date Issued: </span><span className="font-medium">{safeDate(estimate.date)}</span></p>
            {estimate.expiryDate && (
              <p><span className="text-muted-foreground">Valid Through: </span><span className="font-medium">{safeDate(estimate.expiryDate)}</span></p>
            )}
          </div>
        </div>
      </div>

      {/* Project Details */}
      <div className="grid grid-cols-2 gap-6 text-sm">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Prepared For</p>
          <p className="font-semibold">{estimate.customerName ?? "—"}</p>
          {estimate.projectName && <p className="text-xs text-muted-foreground mt-0.5">Project: {estimate.projectName}</p>}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Project Location</p>
          <p className="font-medium whitespace-pre-wrap">{estimate.projectAddress ?? "Address on file"}</p>
        </div>
      </div>

      {/* Scope Summary */}
      {scope && (
        <div className="border-l-4 border-primary pl-4 py-1">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Scope Summary</p>
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{scope}</p>
        </div>
      )}

      {/* Base Bid Sections */}
      {base.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b border-foreground pb-1">
            <h3 className="text-sm font-bold tracking-wider uppercase">Base Bid — Scope of Work</h3>
          </div>
          {base.map((section) => (
            <ProposalSection key={section.id} section={section} />
          ))}
        </div>
      )}

      {/* Alternates */}
      {alternates.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between border-b border-foreground pb-1">
            <h3 className="text-sm font-bold tracking-wider uppercase">Alternates — Add / Deduct</h3>
          </div>
          <p className="text-[11px] text-muted-foreground italic">
            The following items are offered as alternates and are not included in the Base Bid above.
          </p>
          {alternates.map((section) => (
            <ProposalSection key={section.id} section={section} isAlternate />
          ))}
        </div>
      )}

      {/* Pricing Summary */}
      <div className="border border-foreground rounded-md">
        <div className="bg-muted/40 px-4 py-2 border-b border-foreground">
          <p className="text-[10px] uppercase tracking-wider font-bold">Pricing Summary</p>
        </div>
        <div className="px-4 py-3 space-y-1 text-sm">
          {base.map((s) => (
            <div key={s.id} className="flex justify-between">
              <span className="text-muted-foreground">{s.name}</span>
              <span className="font-mono">{fmtMoney(s.subtotal)}</span>
            </div>
          ))}
          <div className="flex justify-between border-t-2 border-foreground pt-2 mt-2 font-bold text-base">
            <span>TOTAL BASE BID</span>
            <span className="font-mono">{fmtMoney(baseTotal)}</span>
          </div>
          {alternates.length > 0 && (
            <>
              <div className="border-t pt-2 mt-2">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">Alternates (not included above)</p>
                {alternates.map((s) => (
                  <div key={s.id} className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{s.name}</span>
                    <span className="font-mono">{fmtMoney(s.subtotal)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Clarifications */}
      {clarifications.length > 0 && (
        <div className="space-y-1">
          <p className="text-[10px] uppercase tracking-wider font-bold border-b border-foreground pb-1">Clarifications & Assumptions</p>
          <ul className="text-xs space-y-1 pt-2 pl-4 list-disc">
            {clarifications.map((c, i) => <li key={i} className="leading-relaxed">{c}</li>)}
          </ul>
        </div>
      )}

      {/* Exclusions */}
      {exclusions.length > 0 && (
        <div className="space-y-1">
          <p className="text-[10px] uppercase tracking-wider font-bold border-b border-foreground pb-1">Exclusions</p>
          <ul className="text-xs space-y-1 pt-2 pl-4 list-disc">
            {exclusions.map((e, i) => <li key={i} className="leading-relaxed">{e}</li>)}
          </ul>
        </div>
      )}

      {/* Acceptance */}
      <div className="space-y-3 border-t-2 border-foreground pt-4">
        <p className="text-[10px] uppercase tracking-wider font-bold">Acceptance</p>
        <p className="text-xs leading-relaxed">
          The undersigned accepts this Proposal and authorizes the work described herein. Payment terms are
          Net 30 from date of invoice unless otherwise specified in the governing subcontract agreement.
          This Proposal shall be deemed incorporated into any resulting contract or work order.
        </p>
        <div className="grid grid-cols-2 gap-8 pt-6">
          <div>
            <div className="border-b border-foreground h-6" />
            <p className="text-[10px] text-muted-foreground mt-1">Authorized Signature</p>
          </div>
          <div>
            <div className="border-b border-foreground h-6" />
            <p className="text-[10px] text-muted-foreground mt-1">Printed Name & Date</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProposalSection({ section, isAlternate = false }: { section: ProposalGroupSection; isAlternate?: boolean }) {
  return (
    <div className="border border-border rounded-md overflow-hidden">
      <div
        className="px-3 py-2 border-b border-border flex items-center justify-between"
        style={{ backgroundColor: section.color ? `${section.color}15` : undefined }}
      >
        <div className="flex items-center gap-2">
          {section.color && <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: section.color }} />}
          <span className="text-sm font-bold uppercase tracking-wide">{section.name}</span>
          {isAlternate && <Badge variant="outline" className="text-[9px] uppercase">Alternate</Badge>}
        </div>
        <span className="text-sm font-mono font-semibold">{fmtMoney(section.subtotal)}</span>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
            <th className="text-left font-semibold px-3 py-1.5 w-12">Item</th>
            <th className="text-left font-semibold px-2 py-1.5">Description</th>
            <th className="text-left font-semibold px-2 py-1.5 w-28">Location</th>
            <th className="text-right font-semibold px-2 py-1.5 w-16">Qty</th>
            <th className="text-center font-semibold px-2 py-1.5 w-12">Unit</th>
            <th className="text-right font-semibold px-3 py-1.5 w-24">Amount</th>
          </tr>
        </thead>
        <tbody>
          {section.lines.map((row) => (
            <tr key={row.itemCode} className="border-b border-border/60 last:border-0">
              <td className="px-3 py-2 font-mono text-muted-foreground">{row.itemCode}</td>
              <td className="px-2 py-2 font-medium">{row.description}</td>
              <td className="px-2 py-2 text-muted-foreground">{row.location}</td>
              <td className="px-2 py-2 text-right font-mono">{row.qty.toLocaleString()}</td>
              <td className="px-2 py-2 text-center text-muted-foreground">{row.unit}</td>
              <td className="px-3 py-2 text-right font-mono font-semibold">{fmtMoney(row.totalBilled)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
